import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import { Button, TextField } from "@material-ui/core";
import { useFormik } from "formik";
import * as yup from "yup";
import { Asterisk } from "phosphor-react";
import fieldTemplatesStyles from "./FieldTemplatesStyles";
import CustomInputFieldWithFormik from "../../Utilities/CustomInputFields/CustomInputFieldWithFormik";
import CustomDropdown from "../../Utilities/CustomDropdown/CustomDropdown.component";
import { fpost } from "../../../API/callsAPI";

const CreateOrEditCustomFieldModal = ({
  type,
  handleClose,
  projectId,
  updateRefreshCounter,
  selectedTrackable,
}) => {
  const classes = fieldTemplatesStyles();
  const [dropdownOptions, setDropdownOptions] = useState([]);

  // Validators
  const EditAssetValidationScheme = yup.object({
    name: yup
      .string("String required")
      .label("name")
      .min(1, "Too Short!")
      .max(50, "Maximum Character Limit: 50")
      .required(),
    type: yup.string().required("This is a required field"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      type: "int",
      default_value: "",
    },
    validationSchema: EditAssetValidationScheme,
    onSubmit: async (values) => {
      const addFieldData = {
        url: "trackables/trackable-field/",
        data: {
          ...values,
          project: projectId,
          trackable: selectedTrackable,
          values: dropdownOptions,
        },
      };
      try {
        const res = await fpost(addFieldData);
        if (res.status === 200 || res.status === 201) {
          updateRefreshCounter();
          handleClose();
        }
      } catch (error) {
        console.log(error);
      }
    },
  });

  const handleDropdownChange = (event, value) => {
    setDropdownOptions(value); // Updates the dropdownOptions state
  };

  return (
    <Card
      sx={{
        width: "400px",
        backgroundColor: "#1B1D28",
        color: "white",
        borderRadius: "20px",
        padding: "10px",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <Typography sx={{ fontSize: "16px !important", fontWeight: 600 }}>
          {type === "new" ? "Add Custom Field" : "Edit Custom Field"}
        </Typography>
        <CustomInputFieldWithFormik
          name="name"
          formik={formik}
          isRequired={true}
          label="Field Name"
        />

        <Box>
          <Box display="flex" alignItems="center" gap="6px">
            <Typography>Type</Typography>
            <Asterisk color="red" />
          </Box>
          <CustomDropdown
            value={formik.values.type}
            updateValue={(val) => formik.setFieldValue("type", val)}
            options={[
              { title: "String", value: "string" },
              { title: "Integer", value: "int" },
              { title: "Dropdown", value: "dropdown" },
              { title: "Date", value: "date_time" },
              { title: "Media", value: "media" },
            ]}
            showNone={false}
          />
        </Box>
        {formik.values.type === "dropdown" ? (
          <Box>
            <Typography>Values</Typography>
            <Autocomplete
              multiple
              id="tags-filled"
              options={dropdownOptions}
              defaultValue={[]}
              freeSolo
              onChange={handleDropdownChange} // Handle changes here
              sx={{
                "& .MuiAutocomplete-root": {
                  backgroundColor: "#252A38",
                },
                "& .MuiChip-root": {
                  borderColor: "#606479",
                  color: "white",
                  "& .MuiChip-label": {
                    color: "white",
                  },
                },
                "& .MuiInputBase-root": {
                  backgroundColor: "#252A38",
                  color: "white",
                },
                "& .MuiSvgIcon-root": {
                  color: "white",
                },
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      variant="outlined"
                      label={option}
                      key={key}
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  placeholder="Add Values"
                />
              )}
            />
          </Box>
        ) : (
          ""
        )}
        {formik.values.type === "dropdown" ? (
          <Box>
            <Box display="flex" alignItems="center" gap="6px">
              <Typography>Default Value</Typography>
              <Asterisk color="red" />
            </Box>
            <CustomDropdown
              value={formik.values.default_value}
              updateValue={(val) => formik.setFieldValue("default_value", val)}
              options={
                dropdownOptions?.length
                  ? dropdownOptions?.map((sin) => {
                      return { title: sin, value: sin };
                    })
                  : []
              }
              showNone={false}
            />
          </Box>
        ) : (
          <CustomInputFieldWithFormik
            name="default_value"
            formik={formik}
            isRequired={false}
            label="Default Value"
            isDisabled={false}
          />
        )}
        <CardActions className={classes.cardAction}>
          <Button
            type="submit"
            value="Submit"
            size="small"
            variant="outlined"
            disableElevation
            color="secondary"
            onClick={handleClose}
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Button
            className={classes.done}
            type="submit"
            value="Submit"
            size="small"
            variant="outlined"
            disableElevation
            color="secondary"
            onClick={formik.handleSubmit}
          >
            Add Field
          </Button>
        </CardActions>
      </CardContent>
    </Card>
  );
};

export default CreateOrEditCustomFieldModal;
